import React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, CheckCircle2, Info, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { cn } from '@/lib/utils';
import type { CatalogChannel } from './catalog';
import type { IntegrationState } from './integrationHelpers';

export type MetaTone = 'ok' | 'muted' | 'warning' | 'error';

interface IntegrationCardProps {
    channel: CatalogChannel;
    state: IntegrationState;
    /** One line: provider and the non-secret detail, or what to do when not set up. */
    detail: string;
    /** One line: "Checked 2 hours ago · OK", "Never checked" or the failure message. */
    meta: string;
    metaTone: MetaTone;
    /** Users without edit rights see "View" instead of a button that would 403. */
    canManage: boolean;
    onOpen: () => void;
}

const META_ICON = {
    ok: CheckCircle2,
    muted: Info,
    warning: AlertTriangle,
    error: XCircle,
} as const;

const META_COLOR: Record<MetaTone, string> = {
    ok: 'text-muted-foreground',
    muted: 'text-muted-foreground',
    warning: 'text-warning',
    error: 'text-destructive',
};

/**
 * One service on the overview: icon, name, ONE status, one detail line, one meta line and
 * ONE button (Set up / Manage / Fix). A service that isn't set up gets a dashed border.
 */
export const IntegrationCard: React.FC<IntegrationCardProps> = ({
    channel,
    state,
    detail,
    meta,
    metaTone,
    canManage,
    onOpen,
}) => {
    const { t } = useTranslation('crm');
    const Icon = channel.icon;
    const MetaIcon = META_ICON[metaTone];
    const name = t(channel.name);

    const action =
        state === 'not_set_up'
            ? canManage
                ? { label: t('integrations.actions.setUp'), variant: 'default' as const }
                : null
            : state === 'needs_attention' && canManage
              ? { label: t('integrations.actions.fix'), variant: 'warning' as const }
              : {
                    label: canManage
                        ? t('integrations.actions.manage')
                        : t('integrations.actions.view'),
                    variant: 'outline' as const,
                };

    return (
        <Card
            data-testid={`integration-card-${channel.id}`}
            className={cn(
                'flex h-full flex-col gap-4 p-4 shadow-none',
                state === 'not_set_up' && 'border-dashed bg-transparent'
            )}
        >
            <div className="flex items-start gap-3">
                <span
                    className={cn(
                        'flex h-10 w-10 shrink-0 items-center justify-center rounded-lg',
                        state === 'not_set_up'
                            ? 'bg-muted text-muted-foreground'
                            : 'bg-primary/10 text-primary'
                    )}
                    aria-hidden="true"
                >
                    <Icon className="h-5 w-5" />
                </span>
                <div className="min-w-0 flex-1">
                    <h2 className="truncate text-sm font-semibold text-foreground">{name}</h2>
                    <p className="line-clamp-2 text-xs text-muted-foreground">
                        {t(channel.description)}
                    </p>
                </div>
                <StatusBadge
                    kind="integration"
                    status={state}
                    label={t(`integrations.status.${state}`)}
                />
            </div>

            <div className="min-w-0 flex-1 space-y-1">
                <p className="truncate text-sm text-foreground" title={detail}>
                    {detail}
                </p>
                <p
                    className={cn('flex items-start gap-1.5 text-xs', META_COLOR[metaTone])}
                    title={meta}
                >
                    <MetaIcon className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                    <span className="line-clamp-2">{meta}</span>
                </p>
            </div>

            {action && (
                <Button
                    type="button"
                    variant={action.variant}
                    size="sm"
                    className="self-start"
                    onClick={onOpen}
                    aria-label={`${action.label}: ${name}`}
                >
                    {action.label}
                </Button>
            )}
        </Card>
    );
};

export default IntegrationCard;
